import { CaretRightIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { SidebarGroupLabel } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SidebarSectionHeaderProps {
  /** The section name. Rendered as the collapsible's trigger. */
  label: string;
  /** Current open state — drives the caret's rotation. */
  open: boolean;
  /** The trailing action's glyph, e.g. `<NotePencilIcon />`. Omit for no action. */
  actionIcon?: ReactNode;
  actionLabel?: string;
  actionTooltip?: ReactNode;
  actionDisabled?: boolean;
  onAction?: () => void;
}

/**
 * One collapsible section header for the app sidebar — "Chat", "Post drafts".
 *
 * Extracted because the two were byte-identical copies of the same 30 lines,
 * and the founder's critique applied to both equally. Adding a third section
 * should reuse this, not paste a third copy.
 *
 * THE CRITIQUE THIS IMPLEMENTS (founder, 2026-08-20, against ChatGPT's sidebar
 * side by side): _"our carrot icons are too big, and they're too far away from
 * the end of the text… the plus icon is too big… The plus icon has a big icon
 * group around it. It just looks messy. It shouldn't have our normal app icon
 * or any icon group on those little pluses… It shouldn't show all the time on
 * the row, only when it's hovered."_
 *
 * Four specific changes from what this replaces:
 *
 * 1. THE CARET SITS AGAINST THE WORD. It was `gap-2` (8px) off the end of the
 *    label, which reads as a separate control floating near the text rather
 *    than as that word's own disclosure. ChatGPT's sits nearly touching. Now
 *    `gap-1` (4px) at `size-2.5` rather than `size-3`.
 *
 * 2. NOTHING SHOWS UNTIL THE ROW IS HOVERED. Both affordances were painted at
 *    full strength on every section, permanently — two persistent controls per
 *    section on a surface whose whole job is a quiet list of destinations. They
 *    now fade in on `group-hover`. `focus-within` keeps them reachable by
 *    keyboard, which a pure hover reveal would strand.
 *
 * 3. THE ACTION IS A BARE GLYPH — NO TILE, NO HOVER BOX. It was a
 *    `CircleIconButton`, the app's round tile sized for page headers and
 *    composers; at the scale of a sidebar section label that tile was the
 *    loudest object in the sidebar, which is exactly backwards. The first pass
 *    removed the circle but kept a `hover:bg-sidebar-accent` rectangle, and the
 *    founder caught that too — a filled box appearing under a 14px glyph is a
 *    second object arriving on hover, louder than the thing it highlights.
 *
 * 4. HOVER IS A NUDGE, NOT A JUMP. Opacity only, in three small steps: absent →
 *    60% on row-hover → 100% on the glyph's own hover. The middle step is what
 *    makes the last one a nudge; going straight from hidden to full foreground
 *    read as a flash. Colour stays constant throughout.
 */
export function SidebarSectionHeader({
  label,
  open,
  actionIcon,
  actionLabel,
  actionTooltip,
  actionDisabled,
  onAction,
}: SidebarSectionHeaderProps): JSX.Element {
  return (
    // `group/section` is a NAMED group: the sidebar primitives already
    // establish unnamed `group`s around the whole sidebar (for the
    // icon-collapsible state), and an unnamed `group-hover` here would bind to
    // whichever ancestor won — revealing every section's action the moment the
    // pointer entered the sidebar at all.
    <div className="group/section relative">
      <CollapsibleTrigger asChild>
        <SidebarGroupLabel
          className={cn('h-7 cursor-pointer select-none gap-1', actionIcon && 'pr-7')}
        >
          {label}
          <CaretRightIcon
            className={cn(
              // Same theme-split token as SidebarGroupLabel — this caret is its
              // disclosure affordance, so it tracks the label's own contrast.
              '!size-2.5 text-foreground/70 dark:text-sidebar-foreground/85',
              'opacity-0 transition duration-150 group-hover/section:opacity-100',
              '[@media(hover:none)]:opacity-100',
              'group-focus-within/section:opacity-100',
              // An OPEN section keeps its caret: the rotation is the only thing
              // saying "this is expanded", and hiding it would make an expanded
              // section look identical to a collapsed one whose list happens to
              // be showing.
              open && 'rotate-90 opacity-100',
            )}
          />
        </SidebarGroupLabel>
      </CollapsibleTrigger>
      {actionIcon && (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Outside CollapsibleTrigger so clicking it never toggles the
                section — the two actions sit on the same row and must stay
                independent. */}
            <button
              type="button"
              aria-label={actionLabel}
              disabled={actionDisabled}
              onClick={onAction}
              className={cn(
                'absolute right-0 top-1/2 -translate-y-1/2',
                // No hover background. Founder, 2026-08-20: _"the icon should be
                // only the icon. It shouldn't have a group around it… When I
                // hover over it, it should just slightly change the opacity, not
                // like a super, super big change."_ A filled rectangle appearing
                // under a 14px glyph is a second object arriving on hover, which
                // is louder than the thing it is meant to highlight. The box is
                // gone; the only hover feedback is opacity.
                'inline-flex size-5 items-center justify-center rounded-md',
                '[&_svg]:size-3.5 text-muted-foreground',
                // Three steps, each small: absent -> present-but-quiet ->
                // fully present. The middle step is what makes the last one a
                // nudge rather than a jump.
                'opacity-0 transition-opacity duration-150',
                'group-hover/section:opacity-60',
                '[@media(hover:none)]:opacity-60',
                'hover:opacity-100 focus-visible:opacity-100',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-30',
              )}
            >
              {actionIcon}
            </button>
          </TooltipTrigger>
          {actionTooltip && <TooltipContent side="right">{actionTooltip}</TooltipContent>}
        </Tooltip>
      )}
    </div>
  );
}
